<template>
    <div class="container">
        <div class="chart" ref="chart"></div>
    </div>
</template>

<script>
import * as d3 from "d3";
import debounce from "lodash.debounce";

export default {
    components: {},

    props: {
        dataset: Array,
    },

    data() {
        return {
            colorSets: ["#F2A98A", "#E7DEB7", "#A0CA6E", "#F0C9C3", "#999999"],
            simulation: null,
            diameter: NaN,
            dots: null,
        };
    },

    async mounted() {
        window.d3 = d3;
        this.diameter = this.getDiameter();
        this.renderD3();

        window.addEventListener(
            "resize",
            debounce(() => {
                this.diameter = this.getDiameter();

                // set SVG height
                d3.select("#bubbleChart")
                    .attr("width", this.diameter)
                    .attr("height", this.diameter);

                // resize dots
                this.dots.selectAll("circle").attr("r", (d) => this.rscale(d.votes));

                // resize labels
                this.formatDotLabels();

                // rerender the force layout
                this.splitBubbles();
            }, 300)
        );
    },

    methods: {
        changeNetwork() {
            d3.selectAll("g.dots").attr("transform", (d) => `translate(${d.x}, ${d.y})`);
        },

        rscale(number) {
            let dotsSize = null;
            if (window.innerWidth >= 1024) {
                dotsSize = [30, 80];
            } else if (window.innerWidth >= 768) {
                dotsSize = [25, 70];
            } else if (window.innerWidth >= 414) {
                dotsSize = [20, 50];
            } else {
                dotsSize = [15, 20];
            }

            const rscale = d3
                .scaleLinear()
                .domain([0, d3.max(this.dataset, (d) => +d.votes)])
                .range(dotsSize);
            return rscale(number);
        },

        formatDotLabels() {
            let radius = (idx) => this.dots._groups[0][idx].children[0].getAttribute("r");
            let diameter = (idx) => radius(idx) * 2;
            d3.selectAll("foreignObject")
                .attr("width", (d, idx) => diameter(idx))
                .attr("height", (d, idx) => diameter(idx))
                .attr("x", (d, idx) => -radius(idx))
                .attr("y", (d, idx) => -radius(idx))
                .select("p")
                .style("font-size", (d, idx) => {
                    let circleDiameter = diameter(idx);
                    if (circleDiameter >= 140) {
                        return "16px";
                    } else if (circleDiameter >= 60) {
                        return "14px";
                    } else if (circleDiameter >= 40) {
                        return "12px";
                    } else {
                        return "10px";
                    }
                })
                .style("line-height", "inherit");

            d3.selectAll(".dots").style("display", "initial");
            this.dots._groups[0].forEach((elem, idx) => {
                let label = elem.children[1].children[0];
                let { width, height } = label.getBoundingClientRect();
                let circleDiameter = diameter(idx);
                if (circleDiameter < width || circleDiameter < height) {
                    d3.select(elem).style("display", "none");
                }
            });
        },

        renderD3() {
            const color = d3.scaleOrdinal(this.colorSets);
            let self = this;

            this.diameter = this.getDiameter();

            let svg = d3
                .select(this.$refs.chart)
                .append("svg")
                .attr("width", this.diameter)
                .attr("height", this.diameter)
                .attr("id", "bubbleChart")
                .attr("class", "chart")
                .style("background", `url(${require("@/assets/visuals/bubble-chart-bgd.svg")}) no-repeat center`);
            // .style("background-size", "contain");

            this.dots = svg
                .selectAll(".dots")
                .data(this.dataset)
                .enter()
                .append("g")
                .attr("class", "dots");

            this.dots
                .append("circle")
                .attr("r", (d) => this.rscale(d.votes))
                .attr("fill", (d, idx) => color(this.colorSets[idx % 5]));

            // foreignObject doesn't work with IE
            this.dots
                .append("foreignObject")
                .append("xhtml:div")
                .attr(
                    "style",
                    "border-radius:360%;min-height:100%;display:flex;justify-content:center;align-items:center;white-space:pre-wrap;"
                )
                .append("xhtml:p")
                .text((d) => d.name + "\n" + d.votes)
                .attr("style", "color:white;text-align:center;font-weight:bold;");

            this.formatDotLabels();

            this.simulation = d3.forceSimulation();
            this.simulation.force("x", this.createAllForces().x);
            this.simulation
                .force("y", this.createAllForces().y)
                .force(
                    "collision",
                    d3.forceCollide((d) => this.rscale(d.votes) + 2)
                )
                .nodes(this.dataset)
                .on("tick", this.changeNetwork);

            d3.select(self.frameElement).style("height", this.diameter + "px");
        },

        createAllForces() {
            return {
                x: d3.forceX().x(this.diameter / 2),
                y: d3.forceY().y(this.diameter / 2),
            };
        },

        splitBubbles() {
            this.simulation
                .force("center", null)
                .force(
                    "collision",
                    d3.forceCollide((d) => this.rscale(d.votes) + 2)
                )
                .alphaDecay(0.0005)
                .velocityDecay(0.6);
            this.simulation.alpha(0.1).restart();
        },

        getDiameter() {
            const chart = this.$refs.chart;
            const oldChart = d3.select("#bubbleChart");
            const containerWidth = (chart && chart.clientWidth) || oldChart.clientWidth;
            return Math.min(containerWidth, 400);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/styles/_main.scss";

.container {
    min-width: 500px;
    max-width: 600px;
}

@media only screen and (max-width: $breakpoint-lg-medium) {
    .chart {
        display: flex;
        justify-content: center;
    }

    .container {
        width: 100%;
    }
}

@media only screen and (max-width: $breakpoint-small) {
    .container {
        width: 100%;
    }
}
</style>
